import { CountyInsightsPanel } from "@/components/site/CountyInsightsPanel";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { getState as getStateForCounty } from "@/data/states";
import { absoluteUrl } from "@/lib/site-origin";
import { fitTitle, fitDescription } from "@/lib/seo-head";
import { ExpertInsight } from "@/components/site/SeoBlocks";
import { buildCityExpertInsight } from "@/data/expert-insights";
import { states as ALL_STATES } from "@/data/states";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { QuickAnswer, PageOutro } from "@/components/site/PageEssentials";
import {
  Section,
  SectionHeader,
  FAQ,
  Breadcrumbs,
  CTABand,
  StatRow,
  TableOfContents,
  InlineCTA,
  LoanGuidelines,
} from "@/components/site/Blocks";
import { RentalIncomeCompare } from "@/components/site/RentalIncomeCompare";
import { DataSources } from "@/components/site/DataSources";
import { MarketUpdateSection } from "@/components/site/MarketUpdateSection";
import { LandlordLawPanel } from "@/components/site/LandlordLawPanel";
import { StrRevenueCalculator } from "@/components/site/StrRevenueCalculator";
import { AirbnbStrExtras } from "@/components/site/AirbnbStrExtras";
import { StrReportCtaBanner } from "@/components/site/StrReportCtaBanner";
import { getProductHero } from "@/data/productHeroes";
import { getCityHero } from "@/data/city-heroes";
import { findCity, publishedInvestorCities, type InvestorCity } from "@/data/investor-cities";
import { requiresEntity } from "@/data/licensed-states";
import { getMarketStats, type MarketStats } from "@/lib/market-stats.functions";
import {
  localBusinessJsonLd,
  cityArticleJsonLd,
  faqJsonLd,
  breadcrumbListJsonLd,
  speakableJsonLd,
  serviceJsonLd,
} from "@/lib/seo-schema";
import { cityKeywordsMeta } from "@/lib/page-keywords";

const SLUG = "airbnb-short-term-rental-loans";

function fmtCurrency(n: number | null | undefined): string | null {
  if (n == null) return null;
  return `$${Intl.NumberFormat("en-US").format(Math.round(n))}`;
}

function fmtPopulation(n: number | null | undefined): string | null {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return Intl.NumberFormat("en-US").format(n);
}

function withLiveStats(city: InvestorCity, stats: MarketStats | null): InvestorCity & { liveVintage?: string | null } {
  if (!stats) return city;
  return {
    ...city,
    medianHomeValue: fmtCurrency(stats.medianHomeValue) ?? city.medianHomeValue,
    population: fmtPopulation(stats.population) ?? city.population,
    liveVintage: stats.vintage,
  };
}

export const Route = createFileRoute("/airbnb-short-term-rental-loans/$state/$city")({
  loader: async ({ params }) => {
    const raw = findCity(params.state, params.city);
    if (!raw) throw notFound();
    const countyInsights = await getCountyInsights({
      data: {
        stateAbbrev: getStateForCounty(params.state)?.abbrev ?? "",
        countyName: raw.county,
        placeLabel: `${raw.name}, ${raw.stateName}`,
      },
    }).catch(() => null);
    const stats = await getMarketStats({
      data: { stateSlug: params.state, citySlug: params.city },
    }).catch(() => null);
    return { city: withLiveStats(raw, stats), liveVintage: stats?.vintage ?? null, marketStats: stats, countyInsights };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "City not found" }, { name: "robots", content: "noindex" }] };
    }
    const c = loaderData.city;
    const title = fitTitle(`Airbnb Loans in ${c.name}, ${c.stateName} | Short-Term Rental Financing`);
    const desc = `Airbnb & short-term rental loans in ${c.name}, ${c.stateName}: qualify on nightly-rental revenue (AirDNA or T-12), STR permit context, LLC-friendly, 20–25% down. NMLS #2620881.`.slice(0, 158);
    const url = `/${SLUG}/${params.state}/${params.city}`;
    const hero = getCityHero(params.state, params.city, c.name, c.stateName);
    const faqs = strCityFaqs(c.name, c.stateName);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: cityKeywordsMeta(SLUG, c.name, c.stateName) },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://dscrloanmarket.com${url}` },
        ...(hero ? [{ property: "og:image", content: absoluteUrl(hero.src) }, { property: "og:image:alt", content: hero.alt }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(hero ? [{ name: "twitter:image", content: absoluteUrl(hero.src) }] : []),
        { property: "article:published_time", content: "2026-01-01" },
      ],
      links: [
        { rel: "canonical", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "en-us", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "x-default", href: `https://dscrloanmarket.com${url}` },
        ...(hero ? [{ rel: "preload", as: "image", href: hero.src }] : []),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(serviceJsonLd({ url, serviceName: `Airbnb / STR Loans in ${c.name}, ${c.stateName}`, description: desc, state: c.stateName, city: c.name })) },
        { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd({ url, state: c.stateName, city: c.name })) },
        { type: "application/ld+json", children: JSON.stringify(cityArticleJsonLd({ url, headline: `Airbnb Loans in ${c.name}, ${c.stateName}`, description: desc, image: hero?.src })) },
        { type: "application/ld+json", children: JSON.stringify(speakableJsonLd(url, [".tldr", ".faq"])) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbListJsonLd([
              { name: "Home", url: "/" },
              { name: "Airbnb / STR Loans", url: `/${SLUG}` },
              { name: c.stateName, url: `/${SLUG}/${params.state}` },
              { name: c.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: CityStrPage,
  notFoundComponent: () => (
    <SiteShell hideBreadcrumbs>
      <Section>
        <div className="mx-auto max-w-2xl py-16 text-center">
          <h1 className="font-display text-3xl font-bold">City not found</h1>
          <p className="mt-2 text-muted-foreground">
            See our <Link to="/where-we-lend" className="text-brand underline">licensed lending map</Link>.
          </p>
        </div>
      </Section>
    </SiteShell>
  ),
});

function strCityFaqs(city: string, state: string) {
  return [
    { q: `Do you fund Airbnb / STR loans in ${city}, ${state}?`, a: `Yes. We fund business-purpose short-term rental loans in ${city} and throughout ${state} on 1–4 unit residential, warrantable condos, and townhomes with permitted STR use.` },
    { q: `How is Airbnb income qualified for a ${city} loan?`, a: `We use the highest-quality revenue source available: (1) 12 months of PMS / Airbnb statements on the subject, (2) AirDNA or Rabbu projection for the exact address, or (3) local market comps. Underwriting applies a 20–25% vacancy/expense discount before dividing by PITIA to size DSCR.` },
    { q: `What DSCR do I need on a ${city} STR?`, a: `Minimum 1.00 DSCR to qualify; best pricing at 1.10+. In ${city}, seasonally adjusted revenue typically supports 1.10–1.30 DSCR at 75% LTV on well-priced deals with real permit status.` },
    { q: `Are short-term rentals legally permitted in ${city}?`, a: `${city} STR rules can change and vary by zoning district, HOA, and building type — always confirm current municipal rules and HOA covenants with the city and your attorney before making an offer. We underwrite to the address's actual permit status.` },
    { q: `What LTV can I get on a ${city} Airbnb loan?`, a: `Up to 75% on purchases and 70% on refinances for STR-qualified properties, subject to a 700 minimum FICO, DSCR of 1.00 or better, reserves, and property condition. Best pricing usually sits ~5% below the cap.` },
    { q: `Do I need STR operating history to close in ${city}?`, a: `No. New STRs price fine with an AirDNA / Rabbu projection, though a T-12 history on the subject earns a tighter vacancy discount and stronger pricing tier.` },
    { q: `Can foreign nationals buy an Airbnb in ${city}?`, a: `Yes. Foreign nationals and ITIN borrowers can purchase or refinance ${city} STRs with no US credit or US tax returns — up to 75% LTV on purchase and rate/term, and 70% on cash-out.` },
    { q: `Are LLC / entity closings supported in ${city}?`, a: `Yes — LLC, LP, S-Corp, C-Corp, and Series LLC vesting are all supported with a member/manager personal guarantee. Standard for business-purpose STR loans in ${state}.` },
    { q: `What insurance is required for a ${city} STR loan?`, a: `Landlord (DP-3) or STR-specific carrier coverage plus commercial-style liability. We build the STR insurance premium into PITIA at underwriting so DSCR is calculated on real numbers, not a placeholder residential quote.` },
    { q: `Can I cash-out refinance an ${city} STR I already own?`, a: `Yes — cash-out refinances up to 75% LTV are available on seasoned ${city} STRs and are commonly used to redeploy capital into the next property.` },
    { q: `How long does a ${city} STR loan take to close?`, a: `Typical timeline is 15–25 business days once title is opened, appraisal is ordered, and permit / HOA verification is returned.` },
    { q: `Do you finance a mid-term rental (30+ day) strategy in ${city}?`, a: `Yes. Mid-term / furnished-rental income qualifies on a 30–90 day lease or PMS actuals. We can size the loan on either LTR or STR income — whichever qualifies better at target LTV.` },
  ];
}

function CityStrPage() {
  const { city, liveVintage, countyInsights, marketStats } = Route.useLoaderData();
  const entity = requiresEntity(city.stateSlug);
  const hero = getCityHero(city.stateSlug, city.slug, city.name, city.stateName) ?? getProductHero(SLUG);
  const otherCities = publishedInvestorCities
    .filter((c) => c.stateSlug === city.stateSlug && c.slug !== city.slug)
    .slice(0, 8);

  return (
    <SiteShell hideBreadcrumbs productLabel="Airbnb / STR">
      <PageHero
        eyebrow={`${city.name}, ${city.stateName}`}
        title={`Airbnb Loans in ${city.name}, ${city.stateName}`}
        subtitle={`Short-term rental financing for investors buying, refinancing, or scaling STRs in ${city.name}. Qualified on nightly revenue, not tax returns.`}
        image={hero}
        primaryHref="/apply?product=str"
      />
      <Section>
        <Breadcrumbs
          items={[
            { name: "Home", href: "/" },
            { name: "Airbnb / STR Loans", href: `/${SLUG}` },
            { name: city.stateName, href: `/${SLUG}/${city.stateSlug}` },
            { name: city.name },
          ]}
        />
      </Section>

      <QuickAnswer
        question={`Can I get an investment property loan in ${city.name}, ${city.stateName}?`}
        answer={`Yes — Simply Approved Mortgages arranges business-purpose financing on non-owner-occupied investment property in ${city.name}, ${city.stateName}, qualified on the property's rental cash flow rather than personal tax returns. Every figure on this page is an estimate and remains subject to a complete application, credit and property review, appraisal where required, and full underwriting.`}
      />
      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Market snapshot" },
          { href: "#revenue", label: "Revenue calculator" },
          { href: "#income", label: "Rental income" },
          { href: "#guidelines", label: "Loan guidelines" },
          { href: "#regulations", label: "STR regulations" },
          { href: "#landlord", label: "Landlord law" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <Section id="tldr">
        <div className="tldr rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 md:p-8">
          <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-brand">
            TL;DR — Airbnb / STR loans in {city.name}
          </div>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li>• Median home value: <strong>{city.medianHomeValue}</strong></li>
            <li>• Median 2BR long-term rent: <strong>{city.medianRent2BR}</strong></li>
            <li>• Cap-rate band (LTR baseline): <strong>{city.capRateBand}</strong></li>
            <li>• STR income accepted: <strong>T-12 PMS or AirDNA</strong></li>
            <li>• Min DSCR: <strong>1.00 (best at 1.10+)</strong></li>
            <li>• Max LTV: <strong>up to 75% purchase / 70% refinance</strong></li>
            <li>• Vesting: <strong>LLC, LP, S/C-Corp</strong>{entity ? " (required in this state)" : ""}</li>
            <li>• Typical close: <strong>15–25 business days</strong></li>
          </ul>
        </div>
      </Section>

      <Section id="snapshot">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <StatRow
              items={[
                { label: "Population", value: city.population },
                { label: "Median home value", value: city.medianHomeValue },
                { label: "Median 2BR (LTR)", value: city.medianRent2BR },
                { label: "Centroid ZIP", value: city.centroidZip },
              ]}
            />
            {liveVintage && (
              <p className="mt-2 text-xs text-muted-foreground">
                Population and median home value live from U.S. Census ACS {liveVintage}. Nightly-rental figures below are illustrative; a licensed loan officer will underwrite to your subject address's real AirDNA / T-12 data.
              </p>
            )}
            <div className="prose max-w-none">
              <h2 className="font-display text-2xl font-bold text-brand-dark">{city.name} STR market snapshot</h2>
              <p className="mt-3 text-muted-foreground">{city.narrative}</p>
              <p className="mt-3 text-muted-foreground">
                Comparing options? See our{" "}
                <Link to="/airbnb-short-term-rental-loans" className="font-semibold text-brand underline">
                  Airbnb / STR loan program
                </Link>{" "}
                or the{" "}
                <Link to="/airbnb-short-term-rental-loans/$state" params={{ state: city.stateSlug }} className="font-semibold text-brand underline">
                  {city.stateName} STR guide
                </Link>{" "}
                for state-level rules and other STR markets we lend in.
              </p>
            </div>
            {entity && (
              <div className="rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-5 text-sm">
                <strong className="text-brand-dark">Entity vesting required in {city.stateName}.</strong>{" "}
                Business-purpose STR loans in this state must vest in an LLC, LP, or similar entity.
              </div>
            )}
          </div>
          <div className="space-y-6">
            <div id="lead-form" className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display text-lg font-bold text-foreground">Get STR pricing for {city.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">A licensed loan officer replies with an initial scenario review once we have your credit authorization and supporting documents — usually same day. Formal pricing and terms follow underwriting.</p>
              <Link to="/apply" search={{ product: "str" }} className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold text-primary hover:bg-brand-gold-hover">
                Start My Approval
              </Link>
              <Link to="/rates" className="mt-2 inline-flex w-full items-center justify-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-brand hover:text-brand">
                See Today's Rates
              </Link>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 text-xs text-muted-foreground">
              <div className="font-semibold text-foreground">Reviewed by our STR desk — permits, ordinances, and revenue docs checked before appraisal</div>
              <div className="mt-1">Licensed loan officer team · NMLS #2620881</div>
              <PageDatesBadge only="reviewed" className="mt-2" />
            </div>
            {otherCities.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Other {city.stateName} STR markets</h3>
                <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  {otherCities.map((c) => (
                    <li key={`${c.stateSlug}-${c.slug}`}>
                      <Link
                        to="/airbnb-short-term-rental-loans/$state/$city"
                        params={{ state: c.stateSlug, city: c.slug }}
                        className="text-brand hover:underline"
                      >
                        {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section>
        <StrReportCtaBanner variant="full" locationLabel={`${city.name}, ${city.stateName}`} />
      </Section>

      <Section id="revenue">
        <SectionHeader
          eyebrow={`${city.name} STR revenue`}
          title={`Model a ${city.name} Airbnb DSCR in seconds`}
          intro={`Plug in an average nightly rate and occupancy for ${city.name} — the calculator applies a typical vacancy and expense discount before sizing DSCR.`}
        />
        <div className="mt-6">
          <StrRevenueCalculator productKey="str" />
        </div>
        <StrReportCtaBanner variant="inline" locationLabel={`${city.name}`} />
      </Section>

      <Section id="income">
        <RentalIncomeCompare
          zip={city.centroidZip}
          locationLabel={`${city.name}, ${city.stateName}`}
          medianRent2BR={city.medianRent2BR}
          medianHomeValue={city.medianHomeValue}
          airbnbAdr={city.airbnbAdr}
          airbnbOccupancy={city.airbnbOccupancy}
        />
        <div className="mt-6">
          <DataSources locationLabel={`${city.name}, ${city.stateName}`} />
        </div>
      </Section>

      <InlineCTA
        title={`Price a ${city.name} Airbnb deal`}
        body="Send the subject address, purchase price or payoff, and AirDNA or T-12 revenue — a licensed LO replies with real pricing."
        cta="Get Your Personalized Rate Quote"
        href="/apply?product=str"
      />

      <Section id="guidelines">
        <LoanGuidelines
          productLabel={`Airbnb / STR · ${city.name}, ${city.stateName}`}
          rows={[
            { label: "Loan amount", value: "$100k – $3M+", why: "Sized to appraised value and program caps." },
            { label: "Max LTV", value: "Up to 75% purchase / 70% refinance", why: "Best pricing typically ~5% below the cap." },
            { label: "Min DSCR", value: "1.00 (best at 1.10+)", why: "STR net monthly income (after vacancy) ÷ PITIA." },
            { label: "Income source", value: "T-12 PMS, AirDNA / Rabbu, or 1007 LTR fallback", why: "Highest-quality verifiable source wins the tightest discount." },
            { label: "Reserves", value: "6 months PITIA", why: "Verified in a bank statement post-close; STR files often held to 9 months where seasonality is heavier." },
            { label: "Terms", value: "30-yr fixed, 5/6 & 7/6 ARM, I/O options", why: "Rate/term picked to match hold period." },
            { label: "Vesting", value: entity ? "LLC / LP / S-Corp / C-Corp (entity required)" : "Individual or LLC / LP / S-Corp / C-Corp", why: "Business-purpose loans close in your entity of choice." },
          ]}
          note="Ranges are typical program guidelines — not a commitment to lend. Final terms are set in an initial scenario review (formal pricing after underwriting) after underwriting review."
        />
      </Section>

      <Section id="regulations">
        <AirbnbStrExtras />
      </Section>

      <Section id="landlord">
        <LandlordLawPanel stateSlug={city.stateSlug} stateName={city.stateName} />
      </Section>

      <MarketUpdateSection productSlug={SLUG} productLabel={`Airbnb / STR · ${city.name}`} />

      <Section id="faq">
        <SectionHeader
          eyebrow="FAQ"
          title={`${city.name} Airbnb loan questions`}
          intro={`Answers to the most common investor questions about STR loans in ${city.name}, ${city.stateName}.`}
          center
        />
        <div className="faq mt-8">
          <FAQ items={strCityFaqs(city.name, city.stateName)} />
        </div>
      </Section>

      <ExpertInsight insight={buildCityExpertInsight(city.name, city.stateName, "airbnb-short-term-rental-loans", "STR loan", ALL_STATES.find((s) => s.name === city.stateName))} />

      <CTABand
        title={`Ready for real pricing on your ${city.name} Airbnb deal?`}
        body={`Complete the short pre-qualification form and upload your supporting documents, and a licensed loan officer will follow up with an initial scenario review for ${city.name}, ${city.stateName}.`}
      />
      <LocalMarketNews
        stateName={city.stateName}
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        cityName={city.name}
        countyName={city.county ?? null}
        stats={marketStats ?? null}
        statsScope="city"
        countyInsights={countyInsights ?? null}
      />
      <CountyInsightsPanel
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        stateName={city.stateName}
        countyName={city.county}
        placeLabel={`${city.name}, ${city.stateName}`}
        initialData={countyInsights}
      />

      <PageOutro hideInsight />
    </SiteShell>
  );
}
